import { Typography } from "@material-ui/core";
import { Fragment, useContext } from "react";
import { asyncForEach, formatSex } from "..";
import { AltaContext, EndosoContext } from "pages/endoso/context";
import { endoso } from "../../../../services";
import { AuthContext } from "context/context";

const ErrorComponent = ({ errors, persons }) => {
  return (
    <Fragment>
      {persons.length === 0 ? (
        <Typography>
          Para generar el endoso se debe agregar al menos un asegurado nuevo
        </Typography>
      ) : (
        <Typography>
          Las siguientes personas no pudieron ser agregadas:
        </Typography>
      )}
      {errors?.map((person, index) => {
        const safeError =
          typeof person?.error === "string"
            ? person.error.replace(/null\s/g, "")
            : person?.error?.mensaje ||
              "Ocurrio un error al guardar el asegurado";

        return (
          <div key={index}>
            {person?.segundoNombre ? (
              <Typography>{`${person?.primerNombre ?? ""} ${
                person?.segundoNombre ?? ""
              } ${person?.apellidoPaterno ?? ""} ${
                person?.apellidoMaterno ?? ""
              }: ${safeError}`}</Typography>
            ) : (
              <Typography>{`${person?.primerNombre ?? ""} ${
                person?.apellidoPaterno ?? ""
              } ${person?.apellidoMaterno ?? ""}: ${safeError}`}</Typography>
            )}

            <br />
          </div>
        );
      })}
    </Fragment>
  );
};

const errorText = {
  emply: "Para generar el endoso se debe agregar al menos un asegurado nuevo",
  default: "Ocurrio un error al guardar el asegurado",
  invalidType:
    "El archivo no tiene una extensión permitida, por favor verifique.",
};

const TEACHER_CATEGORY_605 = "005";

const normalizeCategory605 = (category) => {
  if (category === null || category === undefined || category === "") return "";
  return category.toString().padStart(3, "0");
};

const isTeacherCategory605 = (category) =>
  normalizeCategory605(category) === TEACHER_CATEGORY_605;

const hasCategory605 = (category) => normalizeCategory605(category) !== "";

const isTeacherPerson605 = (person) => {
  const categoryDescription = normalizeText(person?.categoriaDesc);
  return (
    isTeacherCategory605(person?.categoria) ||
    isTeacherCategory605(person?.newCategory) ||
    categoryDescription.includes("docente")
  );
};

const hasPersonCategory605 = (person) =>
  hasCategory605(person?.categoria) ||
  hasCategory605(person?.newCategory) ||
  hasCategory605(person?.categoriaDesc);

const normalizeText = (text) => {
  // `text == null` cubre null y undefined: el backend responde `mensaje: null`
  // en el caso exitoso y `null.toString()` lanzaria TypeError.
  if (text == null) return "";
  return text
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
};

// carga_605 devuelve la categoria recalculada. Backend la envia como
// `categoria` en algunos ambientes y como `categoría` (acentuada) en otros,
// por eso se leen ambas grafias.
const readCategoria = (responseData) =>
  responseData?.categoria ?? responseData?.["categoría"] ?? null;

const isTraditionalChannelMessage = (message) => {
  const normalizedMessage = normalizeText(message);
  return (
    normalizedMessage.includes("tradicional") ||
    normalizedMessage.includes("canal tradicional") ||
    normalizedMessage.includes("proceso tradicional")
  );
};

export const useRulesBy12 = () => {
  const { setBadPersons, file } = useContext(AltaContext);
  const { setShowContinue, setPersons, persons } = useContext(AuthContext);
  const { createMessage, setShowButtons } = useContext(EndosoContext);

  /* methods */
  const rulesByTwelve = async (
    solicitud,
    data,
    setMessage,
    setShowMessage,
    formDta,
    setAlertData,
    dataSend
  ) => {
    let alertShown = false;

    if (!file) {
      const saveEndoso = async (changeType = false) => {
        const removedPersons = [];
        const personsCopy = Array.isArray(persons) ? [...persons] : [];
        let personsForSave = persons;

        let mensajeConfirmacion = null;

        // Orden de envío: primero alumnos/asegurados, luego docentes (categoria "005").
        // Se conserva el índice original para mapear la respuesta a `persons`/`personsForSave`.
        const orderedPersons = personsCopy
          .map((person, originalIndex) => ({ person, originalIndex }))
          .sort((a, b) => {
            const aDocente = isTeacherPerson605(a.person) ? 1 : 0;
            const bDocente = isTeacherPerson605(b.person) ? 1 : 0;
            return aDocente - bDocente;
          });

        const altasFunction = async ({ person, originalIndex }) => {
          const categoriaPayload =
            changeType && normalizeCategory605(person.categoria) === "002"
              ? 3
              : person.categoria;

          const body = {
            tipoIdentificador: "0",
            primerNombre: person.primerNombre,
            segundoNombre: person.segundoNombre || null,
            nombre: person.nombre,
            apPaterno: person.apellidoPaterno,
            apMaterno: person.apellidoMaterno,
            fchNacimiento: person.fechainicial,
            sexo: person.sexo,
            rfc: null,
            razonSocial: "FISICA",
            fechaNacimiento: true,
            oficina: data.oficina,
            ramo: data.ramo,
            poliza: data.poliza,
            solicitud: formDta.noSolicitud,
            categoria: categoriaPayload ?? null,
          };

          const checkResp = await endoso.carga605(body);
          const { codigo, mensaje, error } = checkResp?.data || {};
          const categoria = readCategoria(checkResp?.data);

          const hasRealError =
            (typeof error === "string" && error.trim() !== "") ||
            (typeof error === "number" && error !== 0);

          const isTraditionalMessage = isTraditionalChannelMessage(mensaje);

          if (hasRealError) {
            removedPersons.push({
              ...persons[originalIndex],
              pos: originalIndex,
              error,
            });
            return 0;
          } else if (isTraditionalMessage) {
            removedPersons.push({
              ...persons[originalIndex],
              pos: originalIndex,
              error: mensaje,
            });
            return 0;
          } else if (mensaje && mensaje !== "") {
            personsForSave[originalIndex] = {
              ...person,
              newCategory: categoria,
            };
            if (!mensajeConfirmacion) {
              mensajeConfirmacion = mensaje;
            }
            return 1;
          } else if (codigo === 1) {
            personsForSave[originalIndex] = {
              ...person,
              newCategory: categoria,
            };
            return 1;
          } else {
            removedPersons.push({
              ...persons[originalIndex],
              pos: originalIndex,
              error: mensaje,
            });
            return 0;
          }
        };

        const responseSaves = await asyncForEach(orderedPersons, altasFunction);

        const filteredPersons = personsForSave
          .map((person, index) => {
            const removedPerson = removedPersons.find(
              (item) => item.pos === index
            );
            return removedPerson ? null : person;
          })
          .filter((person) => person !== null);

        const proceedWithEndorsement = async () => {
          const diferenciaResponse = await endoso.diferencia({
            solicitud: formDta.noSolicitud,
          });

          if (diferenciaResponse?.data?.codigo === "1") {
            setPersons(
              filteredPersons.map((person) => ({
                ...person,
                importeActual: diferenciaResponse?.data.importeActual,
                importeAnterior: diferenciaResponse?.data.importeAnterior,
                importeDiferencia: diferenciaResponse?.data.importeDiferencia,
              }))
            );

            const clausulaResponse = await endoso.clausula({
              solicitud: formDta.noSolicitud,
            });
            if (clausulaResponse?.data?.codigo === "1") {
              setShowButtons((prev) => ({ ...prev, infoTable: true }));
            } else {
              setMessage({
                text:
                  clausulaResponse?.data?.mensaje ||
                  "Ocurrio un error de conexion",
                severity: "warning",
              });
              setShowMessage(true);
            }
          } else {
            setMessage({
              text:
                diferenciaResponse?.data?.mensaje ||
                "Ocurrio un error de conexion",
              severity: "warning",
            });
            setShowMessage(true);
          }
        };

        if (responseSaves.find((save) => save === 1)) {
          if (removedPersons.length > 0) {
            setPersons(filteredPersons);
            setMessage({
              text: (
                <ErrorComponent errors={removedPersons} persons={persons} />
              ),
              severity: "warning",
            });
            setShowMessage(true);
          }

          if (mensajeConfirmacion) {
            setAlertData({
              message: mensajeConfirmacion,
              successText: "Continuar",
              cancelText: "Cancelar",
              action: proceedWithEndorsement,
            });
          } else {
            await proceedWithEndorsement();
          }
        } else {
          setMessage({
            text: <ErrorComponent errors={removedPersons} persons={persons} />,
            severity: "warning",
          });
          setShowMessage(true);
        }
      };

      const getNumberOfPersons = () => {
        if (!Array.isArray(persons)) {
          return {
            alumnos: 0,
            docentes: 0,
          };
        }

        const numberOfTeachers = persons.filter(isTeacherPerson605).length;
        const numberOfStudents = persons.filter(
          (person) =>
            hasPersonCategory605(person) && !isTeacherPerson605(person)
        ).length;

        return {
          alumnos: numberOfStudents,
          docentes: numberOfTeachers,
        };
      };

      const checkPrima605 = async () => {
        const body = {
          oficina: data.oficina,
          poliza: data.poliza,
          ramo: data.ramo,
          ...getNumberOfPersons(),
        };

        return endoso.check605(body);
      };

      const checkResponse = await checkPrima605();
      const { codigo, mensaje } = checkResponse?.data || {};
      const saveWithNewType = async () => await saveEndoso(true);

      const hasMensaje = typeof mensaje === "string" && mensaje.trim() !== "";

      if (hasMensaje) {
        if (isTraditionalChannelMessage(mensaje)) {
          setAlertData({
            message: mensaje,
            successText: "Continuar",
            cancelText: "Cancelar",
          });
          return;
        }

        // codigo:1 con mensaje "cobro en prima" → confirmar antes de cargar
        setAlertData({
          message: mensaje,
          successText: "Continuar",
          cancelText: "Cancelar",
          action: saveWithNewType,
        });
      } else if (codigo === 1) {
        // codigo:1, mensaje null → todo bien → carga directa
        await saveEndoso();
      } else {
        // codigo distinto de 1 sin mensaje → error genérico
        setMessage({
          text: mensaje || "Ocurrio un error de conexion",
          severity: "warning",
        });
        setShowMessage(true);
      }
    } else {
      // guardar archivo
      const enviarSituacionsIndividualmente = async (situacions) => {
        const resultados = [];

        for (const situacion of situacions) {
          try {
            const altaRes = await endoso.carga605(situacion);
            const altaMensaje = altaRes?.data?.mensaje;
            const altaAlerta = altaRes?.data?.alerta;
            const hasAltaMensaje =
              typeof altaMensaje === "string" && altaMensaje.trim() !== "";
            const hasAltaAlerta =
              typeof altaAlerta === "string" && altaAlerta.trim() !== "";

            if (
              hasAltaMensaje &&
              isTraditionalChannelMessage(altaMensaje) &&
              !alertShown
            ) {
              setAlertData({
                message: altaMensaje,
                successText: "Continuar",
                cancelText: "Cancelar",
              });
              alertShown = true;
              resultados.push({
                ...situacion,
                saved: false,
                error: altaMensaje,
              });
              continue;
            }

            if (hasAltaMensaje && !alertShown) {
              createMessage({
                text: altaMensaje,
                variant: "info",
                action: true,
              });
              alertShown = true;
            }
            resultados.push({
              ...situacion,
              saved: altaRes?.data?.codigo === 1,
              changeCategory: altaRes.data.mensaje ? true : false,
              newCategory: readCategoria(altaRes?.data),
            });

            if (altaRes?.data?.codigo === 1 && hasAltaAlerta && !alertShown) {
              if (isTraditionalChannelMessage(altaAlerta)) {
                setAlertData({
                  message: altaAlerta,
                  successText: "Continuar",
                  cancelText: "Cancelar",
                });
                alertShown = true;
                setShowContinue(false);
                continue;
              }

              createMessage({
                text: altaAlerta,
                variant: "info",
                action: true,
              });
              alertShown = true;
              setShowContinue(true);
            } else if (altaRes?.data.codigo !== 1) {
              createMessage({
                text: altaRes?.data?.mensaje || errorText.default,
                variant: "warning",
              });
            }
          } catch (error) {
            resultados.push({ ...situacion, saved: false });
          }
        }

        return resultados;
      };
      const fileExtension = file.name.split(".").pop();
      if (fileExtension === "xlsx") {
        setBadPersons([]);
        const uploadResponse = await endoso.uploadFile(
          file,
          data.oficina,
          data.ramo,
          data.poliza,
          solicitud
        );
        if (uploadResponse.data?.codigo === "1") {
          // altas605 puede venir null/undefined cuando el back rechaza todas
          // las personas del archivo; se normaliza para no romper el for...of.
          const situacions = uploadResponse.data?.altas605 ?? [];
          const uploadMensaje = uploadResponse.data?.mensaje;

          // Personas rechazadas por el back (p. ej. fuera del límite de edad).
          // Se muestran en la tabla "Errores en el archivo" aunque otras sí se guarden.
          const errores = uploadResponse.data?.errores ?? [];
          if (errores.length) {
            setBadPersons(
              errores.map((e) => ({ nombre: e.nombre, error: e.mensaje }))
            );
          }
          const hasUploadMensaje =
            typeof uploadMensaje === "string" && uploadMensaje.trim() !== "";

          // Ejecuta la carga real de las situaciones. Se invoca directo cuando
          // no hay mensaje, o desde el modal de confirmación cuando sí lo hay.
          const runCarga = async () => {
            const savedSituacions = await enviarSituacionsIndividualmente(
              situacions
            );
            const situacionsFinished = savedSituacions?.filter(
              ({ saved }) => saved
            );
            if (situacionsFinished.length) {
              // Estas personas ya quedaron guardadas en el back: la tabla se
              // muestra SIEMPRE, aunque diferencia/clausula fallen (en ese
              // caso los importes quedan vacíos y se avisa con un warning).
              let importes = {};
              try {
                const diferenciaRes = await endoso.diferencia({
                  solicitud: solicitud ?? "",
                });
                const codigo = diferenciaRes?.data?.codigo;
                if (codigo === 1 || codigo === "1") {
                  importes = {
                    importeActual: diferenciaRes?.data?.importeActual,
                    importeAnterior: diferenciaRes?.data?.importeAnterior,
                    importeDiferencia: diferenciaRes?.data?.importeDiferencia,
                  };
                } else
                  createMessage({
                    text: diferenciaRes?.data?.mensaje || errorText.default,
                    variant: "warning",
                  });
              } catch (error) {
                createMessage({
                  text: errorText.default,
                  variant: "warning",
                });
              }

              setPersons(
                situacionsFinished.map((person) => ({
                  ...person,
                  // Las situaciones vienen crudas del back (altas605): se
                  // normalizan al shape que lee la tabla de guardados.
                  nombre:
                    person.nombre ??
                    [
                      person.primerNombre,
                      person.segundoNombre,
                      person.apPaterno ?? person.apellidoPaterno,
                      person.apMaterno ?? person.apellidoMaterno,
                    ]
                      .filter(Boolean)
                      .join(" "),
                  sexo: formatSex(person.sexo),
                  categoriaDesc:
                    person.categoriaDesc ??
                    (isTeacherCategory605(person.categoria)
                      ? "DOCENTE/ADMINISTRATIVO"
                      : undefined),
                  categoria:
                    normalizeCategory605(person.categoria) === "002"
                      ? 3
                      : person.categoria,
                  ...importes,
                }))
              );

              try {
                const clausulaRes = await endoso.clausula({
                  solicitud: solicitud ?? "",
                });
                const codigoClausula = clausulaRes?.data?.codigo;
                if (codigoClausula !== 1 && codigoClausula !== "1")
                  createMessage({
                    text: clausulaRes?.data?.mensaje || errorText.default,
                    variant: "warning",
                  });
              } catch (error) {
                createMessage({
                  text: errorText.default,
                  variant: "warning",
                });
              }

              setShowButtons((prev) => ({ ...prev, infoTable: true }));
            } else {
              if (uploadResponse.data?.errores?.length) {
                const errorMessage =
                  uploadResponse?.data?.errores[0]?.mensaje ?? "";
                createMessage({
                  text: `No se pudieron agregar todas las personas, por favor verifique que los datos sean correctos: ${errorMessage}`,
                  variant: "warning",
                });
                // Ninguna persona se guardó pero sí hubo rechazos: se muestra
                // la vista de resultados para que la tabla de errores sea
                // visible (la tabla de guardados pinta su estado vacío).
                setPersons([]);
                setShowButtons((prev) => ({ ...prev, infoTable: true }));
              }
            }
          };

          if (hasUploadMensaje && isTraditionalChannelMessage(uploadMensaje)) {
            setAlertData({
              message: uploadMensaje,
              successText: "Continuar",
              cancelText: "Cancelar",
            });
          } else if (hasUploadMensaje) {
            // mensaje con valor (sin errores) → confirmar antes de cargar.
            setAlertData({
              message: uploadMensaje,
              successText: "Continuar",
              cancelText: "Cancelar",
              action: runCarga,
            });
          } else {
            // sin mensaje y sin errores → carga automática.
            await runCarga();
          }
        } else {
          if (uploadResponse.data?.errores?.length) {
            const errorMessage =
              uploadResponse?.data?.errores[0]?.mensaje ?? "";
            createMessage({
              text: `No se pudieron agregar todas las personas, por favor verifique que los datos sean correctos: ${errorMessage}`,
              variant: "warning",
            });
          }
        }
      } else {
        createMessage({
          text: errorText.invalidType,
          variant: "warning",
        });
      }
    }
  };

  return { rulesByTwelve };
};
